import React, { useEffect, useRef } from 'react';
import { Text, Animated, TouchableOpacity, StyleSheet } from 'react-native';

interface ToastMessageProps {
  message: string;
  visible: boolean;
  onDismiss: () => void;
  indicator?: string;
}

const INDICATOR_CONFIG: Record<string, { bg: string; border: string; iconColor: string; textColor: string; icon: string }> = {
  success: { bg: '#f0faf1', border: '#388e3c', iconColor: '#388e3c', textColor: '#1b5e20', icon: '✓' },
  fail:    { bg: '#fff5f5', border: '#d32f2f', iconColor: '#d32f2f', textColor: '#b71c1c', icon: '✕' },
  warning: { bg: '#fffde7', border: '#f57c00', iconColor: '#f57c00', textColor: '#e65100', icon: '⚠' },
  info:    { bg: '#f0f7ff', border: '#0078D4', iconColor: '#0078D4', textColor: '#003a8c', icon: 'ℹ' },
};
const DEFAULT_CONFIG = INDICATOR_CONFIG.info;

const ToastMessageAnnimated: React.FC<ToastMessageProps> = ({ message, visible, onDismiss, indicator }) => {
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const slideAnim = useRef(new Animated.Value(20)).current;
  const config = INDICATOR_CONFIG[indicator ?? ''] ?? DEFAULT_CONFIG;

  useEffect(() => {
    if (visible) {
      Animated.parallel([
        Animated.timing(fadeAnim, { toValue: 1, duration: 250, useNativeDriver: true }),
        Animated.timing(slideAnim, { toValue: 0, duration: 250, useNativeDriver: true }),
      ]).start();

      const timer = setTimeout(() => {
        Animated.parallel([
          Animated.timing(fadeAnim, { toValue: 0, duration: 250, useNativeDriver: true }),
          Animated.timing(slideAnim, { toValue: 20, duration: 250, useNativeDriver: true }),
        ]).start(() => onDismiss());
      }, 4000);

      return () => clearTimeout(timer);
    }
  }, [visible]);

  if (!visible) return null;

  return (
    <Animated.View
      style={[
        styles.container,
        {
          backgroundColor: config.bg,
          borderLeftColor: config.border,
          opacity: fadeAnim,
          transform: [{ translateY: slideAnim }],
        },
      ]}
    >
      <Text style={[styles.icon, { color: config.iconColor }]}>{config.icon}</Text>
      <Text style={[styles.message, { color: config.textColor }]} numberOfLines={3}>
        {message}
      </Text>
      <TouchableOpacity onPress={onDismiss} style={styles.closeBtn} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
        <Text style={[styles.closeText, { color: config.iconColor }]}>✕</Text>
      </TouchableOpacity>
    </Animated.View>
  );
};

const styles = StyleSheet.create({
  container: {
    position: 'absolute',
    bottom: 24,
    left: 16,
    right: 16,
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 14,
    borderRadius: 8,
    borderLeftWidth: 4,
    zIndex: 1000,
    // web shadow
    ...({ boxShadow: '0 4px 16px rgba(0,0,0,0.12)' } as any),
  },
  icon: {
    fontSize: 16,
    fontWeight: '700',
    marginRight: 10,
    minWidth: 18,
    textAlign: 'center',
  },
  message: {
    flex: 1,
    fontSize: 14,
    lineHeight: 20,
  },
  closeBtn: {
    marginLeft: 10,
  },
  closeText: {
    fontSize: 13,
    fontWeight: '700',
  },
});

export default ToastMessageAnnimated;
